import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useState, useEffect, useMemo } from 'react';
import useFetch from 'use-http';
import { Calendar, FileText, ListChecks, Loader2, Search, X } from 'src/components/common/icons'; // Adjust path
import { ILeaseMaster } from 'src/models/dlcLease';
import { ILeaseCheck } from 'src/models/dlcLease/ILeaseCheck';
import CheckDetailsModal from 'src/components/invoicing/components/CheckDetailsModal';

// --- Interfaces ---
interface ILeaseChecksDialogProps {
    leaseMaster: ILeaseMaster;
    isOpen: boolean;
    onClose: () => void;
}

// --- Utility Functions (Duplicated here for independent functioning of list) ---
const formatCurrency = (amount: number | null) => (amount !== null ? `$${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '-');

const formatDate = (dateString: string | null) => (dateString ? new Date(dateString).toLocaleDateString() : '-');

// --- Main Component ---
const LeaseChecksDialog: React.FC<ILeaseChecksDialogProps> = ({ leaseMaster, isOpen, onClose }) => {
    const { lsNo } = leaseMaster;
    const [checks, setChecks] = useState<ILeaseCheck[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // State for the specific check selected for details
    const [selectedCheck, setSelectedCheck] = useState<ILeaseCheck | null>(null);

    const { get } = useFetch();

    // API endpoint variable
    const endpoint = `/v1.0/leases/master/${lsNo}/checks`;

    // --- Data Fetching Effect ---
    useEffect(() => {
        if (!isOpen || !lsNo) {
            setChecks([]);
            setSelectedCheck(null); // Clear selection on close
            return;
        }

        const fetchChecks = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await get(endpoint);

                if (!response) {
                    const errorText = await response?.text();
                    throw new Error(`API call failed with status ${response?.status}: ${errorText || 'Unknown error'}`);
                }

                const data: ILeaseCheck[] = response;
                setChecks(data);
            } catch (e) {
                setError(e instanceof Error && e.message.includes('API call failed') ? e.message : 'Failed to retrieve checks from the server.');
                setChecks([]);
            } finally {
                setLoading(false);
            }
        };

        fetchChecks();
        // Reload when the modal or lease changes. use-http's get identity is not a
        // reliable effect dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lsNo, isOpen]);

    // --- Filtering Logic ---
    const filteredChecks = useMemo(() => {
        if (!searchTerm) {
            return checks;
        }
        const lowerCaseSearch = searchTerm.toLowerCase();
        return checks.filter(
            (check) =>
                check.payee?.toLowerCase().includes(lowerCaseSearch) ||
                check.memoFor?.toLowerCase().includes(lowerCaseSearch) ||
                check.checkNo?.toString().includes(lowerCaseSearch)
        );
    }, [checks, searchTerm]);

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <div style={styles.modalOverlay} onClick={onClose}>
                <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div style={styles.modalHeader}>
                        <h2 style={styles.modalTitle}>
                            <ListChecks style={styles.headerIcon} />
                            Check Transaction History for Lease # {lsNo}
                        </h2>
                        <button onClick={onClose} style={styles.closeButton} aria-label="Close dialog">
                            <X style={styles.closeIcon} />
                        </button>
                    </div>

                    {/* Body Content */}
                    <div style={styles.modalBody}>
                        {/* Search Bar */}
                        <div style={styles.searchContainer}>
                            <div style={styles.searchInputWrapper}>
                                <Search style={styles.searchIcon} />
                                <LeaseRecordInput
                                    type="text"
                                    placeholder="Search by Payee, Check No, or Memo"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ ...styles.inputBase, ...(loading || checks.length === 0 ? styles.inputDisabled : {}) }}
                                    disabled={loading || checks.length === 0}
                                />
                            </div>
                        </div>

                        {/* Loading/Error/Empty State */}
                        {loading && (
                            <div style={styles.loadingState}>
                                <Loader2 className="w-8 h-8 animate-spin" style={styles.loaderIcon} />
                                <span style={styles.loadingText}>Fetching Check Data...</span>
                            </div>
                        )}

                        {error && (
                            <div style={styles.errorState}>
                                <FileText style={styles.errorIcon} />
                                Error: {error}
                            </div>
                        )}

                        {!loading && !error && checks.length === 0 && (
                            <div style={styles.emptyState}>
                                <Search style={styles.emptyIcon} />
                                <span style={styles.emptyText}>No checks found for this lease number.</span>
                            </div>
                        )}

                        {/* Checks Table */}
                        {!loading && !error && checks.length > 0 && (
                            <div style={styles.tableWrapper}>
                                <table style={styles.tableBase}>
                                    <thead style={styles.tableHead}>
                                        <tr>
                                            <th style={styles.tableHeaderCell}>Check No</th>
                                            <th style={styles.tableHeaderCell}>Check Date</th>
                                            <th style={styles.tableHeaderCell}>Check Amt.</th>
                                            <th style={styles.tableHeaderCell}>Check Type</th>
                                            <th style={styles.tableHeaderCell}>Void</th>
                                            <th style={styles.tableHeaderCell}>Payee</th>
                                            <th style={styles.tableHeaderCell}>Date Sent</th>
                                        </tr>
                                    </thead>
                                    <tbody style={styles.tableBody}>
                                        {filteredChecks.map((check, index) => {
                                            const isVoid = check.void === 'Y';
                                            const rowStyle = {
                                                ...styles.tableRow,
                                                ...(isVoid ? styles.voidRow : {}),
                                            };

                                            return (
                                                <tr key={check.checkId ?? index} style={rowStyle}>
                                                    {/* CHECK NO: Clickable */}
                                                    <td style={styles.tableDataCell}>
                                                        <button
                                                            style={styles.checkNoButton}
                                                            onClick={() => setSelectedCheck(check)}
                                                            title="View Check Details"
                                                        >
                                                            {check.checkNo}
                                                        </button>
                                                    </td>
                                                    <td style={styles.tableDataCell}>
                                                        <div style={styles.dateCell}>
                                                            <Calendar style={styles.dateIcon} /> {formatDate(check.checkDate)}
                                                        </div>
                                                    </td>
                                                    <td style={{ ...styles.tableDataCell, ...styles.amountCell }}>
                                                        {formatCurrency(check.checkAmt)}
                                                    </td>
                                                    <td style={styles.tableDataCell}>{check.type || '-'}</td>
                                                    <td style={styles.tableDataCell}>
                                                        <LeaseRecordInput
                                                            type="checkbox"
                                                            checked={isVoid}
                                                            disabled={true}
                                                            className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                        />
                                                    </td>
                                                    <td style={styles.tableDataCell}>{check.payee}</td>
                                                    <td style={styles.tableDataCell}>
                                                        <div style={styles.dateCell}>
                                                            <Calendar style={styles.dateIcon} /> {formatDate(check.sendDate)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {filteredChecks.length === 0 && searchTerm && (
                                    <div style={styles.noResults}>No results found for &quot;{searchTerm}&quot;.</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div style={styles.modalFooter}>
                        <button onClick={onClose} style={styles.buttonPrimary}>
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* RENDER THE REUSABLE DETAILS MODAL */}
            {selectedCheck && <CheckDetailsModal check={selectedCheck} onClose={() => setSelectedCheck(null)} />}
        </>
    );
};

// --- Component Styles (Only for List Modal) ---
const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '90%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: { fontSize: '1.5rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', margin: 0 },
    headerIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem', color: '#4F46E5' },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' },
    modalFooter: { padding: '1rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '0.5rem',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.15s ease-in-out',
    },
    searchContainer: { marginBottom: '1rem' },
    searchInputWrapper: { position: 'relative' },
    searchIcon: {
        position: 'absolute',
        left: '0.75rem',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '1.25rem',
        height: '1.25rem',
        color: '#9CA3AF',
    },
    inputBase: {
        width: '100%',
        paddingLeft: '2.5rem',
        paddingRight: '1rem',
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.5rem',
        color: '#111827',
        backgroundColor: '#FFFFFF',
        outline: 'none',
        transition: 'box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out',
    },
    inputDisabled: { backgroundColor: '#F3F4F6', cursor: 'not-allowed', opacity: 0.6 },
    loadingState: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '12.5rem', color: '#4F46E5' },
    loaderIcon: { width: '2rem', height: '2rem', marginRight: '0.5rem', animation: 'spin 1s linear infinite' },
    loadingText: { fontSize: '1.125rem', fontWeight: 500 },
    errorState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '12.5rem',
        color: '#EF4444',
        fontWeight: 500,
        backgroundColor: '#FEF2F2',
        borderRadius: '0.5rem',
        padding: '1rem',
    },
    errorIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '12.5rem',
        color: '#6B7280',
    },
    emptyIcon: { width: '2.5rem', height: '2.5rem', marginBottom: '0.5rem' },
    emptyText: { fontSize: '1.125rem', fontWeight: 500 },
    tableWrapper: { overflowY: 'auto', flexGrow: 1, borderRadius: '0.5rem', border: '1px solid #E5E7EB' },
    tableBase: { minWidth: '100%', borderCollapse: 'collapse' },
    tableHead: { backgroundColor: '#F9FAFB', position: 'sticky', top: 0 },
    tableHeaderCell: {
        padding: '0.75rem 1rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #E5E7EB',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    tableRow: { transition: 'background-color 0.1s ease' },
    voidRow: { backgroundColor: '#FEE2E2' }, // Nice light red for voided rows
    tableDataCell: { padding: '0.75rem 1rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#111827', borderBottom: '1px solid #F3F4F6' },
    checkNoButton: {
        fontWeight: 600,
        color: '#4F46E5',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'underline',
        padding: 0,
    },
    amountCell: { fontWeight: 600 },
    dateCell: { color: '#6B7280', display: 'flex', alignItems: 'center' },
    dateIcon: { width: '1rem', height: '1rem', marginRight: '0.25rem' },
    noResults: { padding: '1rem', textAlign: 'center', color: '#6B7280' },
};

export default LeaseChecksDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/checks/ChecksModal.tsx